(ns practitest.frontend.components.grid
  (:require
    [practitest.frontend.common.dom :as dom]
    [reagent.core :as reagent]
    [reagent.dom]
    [clojure.set]
    [practitest.frontend.components.icons :as icons]
    [practitest.frontend.components.pagination :as pagination]
    [practitest.frontend.components.spinners :as spinners]

    ;; Grid implementation
    [practitest.frontend.components.grid.common :as gc]
    [practitest.frontend.components.grid.filters :as filters]
    [practitest.frontend.components.grid.columns-settings :as columns-settings]
    [practitest.frontend.components.grid.server :as server]
    ;; Fields implementation
    [practitest.frontend.components.grid.fields]
    [practitest.frontend.components.overlay :as overlay]
    [practitest.frontend.components.react-bootstrap :as react-bootstrap]
    [practitest.frontend.components.overflow :as overflow]
    [clojure.string :as str]))

(defn- per-page-input [{:keys [local-state-updating
                               on-query-changed
                               per-page
                               per-page-save
                               total-count]
                        :as   tableprops}]
  ;; had some tough time adapting react-select to BS layout, decided to stick with "pure" select
  [:select.form-select.form-select-sm
   {:on-change (fn [e]
                 (let [new-per-page (js/parseInt (dom/event-value e))]
                   (swap! local-state-updating assoc :per-page new-per-page)
                   (when per-page-save
                     (per-page-save new-per-page))
                   (when on-query-changed
                     (let [current-page     (server/get-current-page tableprops)
                           ;; Special condition for getting out of bounds
                           new-current-page (if (< total-count (* new-per-page current-page))
                                              (inc (int (/ total-count new-per-page)))
                                              current-page)]
                       (on-query-changed (server/build-query
                                           (assoc tableprops
                                             :per-page new-per-page)
                                           new-current-page))))))

    :value     (str per-page)}
   (for [i ["10" "20" "40" "60" "100"]]
     ^{:key i} [:option
                {:value i}
                i])])

(defn pagination-control [{:keys [per-page
                                  current-page
                                  total-count
                                  on-query-changed
                                  set-current-page
                                  current-page-save]
                           :as   tableprops}
                          class]

  [:span.pagination-section
   {:class class}

   [:span.centered.per-page-control
    "Show"

    [:span.mx-2
     [per-page-input tableprops]]

    "rows."]
   [pagination/pagination-view {:total-count  total-count
                                :per-page     per-page
                                :current-page current-page
                                :max-buttons  9
                                :on-change    (fn [page]
                                                (if on-query-changed
                                                  (on-query-changed (server/build-query tableprops page))
                                                  (do
                                                    (when current-page-save
                                                      (current-page-save page))
                                                    (set-current-page page))))}]])

(defn grid-toolbar [{:keys [options
                            local-state-updating
                            extra-toolbar-fn]
                     :as tableprops}]
  [:div.grid-toolbar
   (when extra-toolbar-fn
     [:div.extra-toolbar
      (extra-toolbar-fn tableprops)])

   [:div.main-toolbar
    [:div.left-section.nowrap
     [filters/filters-info-block tableprops]]

    [:div.right-section
     (when (:customizable-columns? options)
       [columns-settings/columns-settings-button tableprops])
     [pagination-control tableprops "upper"]]]])

(defn row-selector [{:keys [selected? disabled? on-change]}]
  [:input.form-check-input
   {:type      "checkbox"
    :checked   @selected?
    :disabled  disabled?
    :on-change on-change}])

(defn header-actions-cell [{{:keys [selectable? actions]} :options
                            page-rows                     :page-rows
                            total-count                   :total-count
                            all-selected?                 :all-selected?
                            toggle-selection-page         :toggle-selection-page}]
  [:div.d-flex.justify-content-between
   [:div.d-flex.justify-content-start.me-2
    (when selectable?
      (let [empty? (= total-count 0)]
        [row-selector {:selected? (if empty?
                                    (atom false)
                                    (all-selected? page-rows))
                       :disabled? empty?
                       :on-change (fn [_]
                                    ;; Toggle for current page only
                                    (toggle-selection-page page-rows))}]))]

   [:div.d-flex.justify-content-end
    (doall
      (for [[i {:keys [header]}] (map-indexed vector actions)]
        ^{:key (str i)} [:span.text-nowrap header]))]])

;; Encapsulates handlers install and cleanup
(defn start-dragging [on-drag on-drag-end]
  (let [on-move (fn [evt]
                  (on-drag (.-clientX evt)))
        drag-end (fn drag-end [evt]
                   (js/document.removeEventListener "mousemove" on-move)
                   (js/document.removeEventListener "mouseup" drag-end)
                   (js/document.removeEventListener "mouseleave" drag-end)

                   (on-drag-end (.-clientX evt)))]
    (js/document.addEventListener "mousemove" on-move)
    (js/document.addEventListener "mouseup" drag-end)
    (js/document.addEventListener "mouseleave" drag-end)))

(def fallback-column-width 150)
(def minimum-column-width 80)

(defn resize-handle [{:keys [local-state-updating
                             columns-widths-save] :as tableprops}
                     column
                     component]
  [:span.column-resize-handle
   {:on-mouse-down (fn [e]
                     ;; Prevent text selection
                     (dom/prevent-default e)

                     ;; save starting X and width
                     (let [thnode (reagent.dom/dom-node component)
                           startX (.-pageX e)
                           startWidth (:width column)
                           calc-new-width (fn [x]
                                            (max minimum-column-width (+ startWidth (- x startX))))
                           column-id (:field column)]
                       (start-dragging
                         (fn [x]
                           ;; Use DOM mutation to speedup table updates here
                           (.setProperty (.-style thnode) "width" (str (calc-new-width x) "px")))
                         (fn [x]
                           ;; Save resulting length in an atom
                           (swap! local-state-updating update :columns-widths assoc column-id (calc-new-width x))
                           (when columns-widths-save
                             (columns-widths-save (:columns-widths @local-state-updating)))))))

    :on-click      (fn [e]
                     ;; Prevent clicking on header
                     (dom/stop-propagation e))}])

;; Auto-tooltip overflown text container
(defn span-column-name
  [text]
  [overflow/overflow-with-tooltip
   (fn [props]
     [:span.column-name props
      text])
   text])

(defn header-cell-fn [{:keys [local-state-updating
                              sort-order-save]
                       :as tableprops}

                      {:keys [sortable?
                              autofilter?]
                       :or   {sortable? true}
                       :as column}]
  ;; Allow override of resizable per-column
  (let [resizable? (if (contains? column :resizable?)
                     (:resizable? column)
                     (:resizable? tableprops))]
    [:th.table-header
     (when resizable?
       {:class "table-header-resizable"
        :style {:width (str (:width column) "px")}})

     (when resizable?
       [resize-handle
        tableprops
        column
        (reagent/current-component)])

     (let [field (:field column)
           [sort-column direction] (:sort-settings @local-state-updating)]
       [:div.sortable-header
        [span-column-name [gc/header-cell column]]

        (when (or sortable?
                  autofilter?)
          [:span.header-controls.ms-auto
           (when sortable?
             (let [sorted? (= sort-column (:field column))]
               [:span.sort-direction
                {:on-click (fn [e]
                             (if (not= field sort-column)
                               (swap! local-state-updating
                                      assoc :sort-settings [field :asc])
                               (swap! local-state-updating
                                      assoc :sort-settings [field (if (= direction :asc)
                                                                    :desc
                                                                    :asc)]))

                             (when sort-order-save
                               (sort-order-save (:sort-settings @local-state-updating)))

                             ;; Resend query when sorting changes
                             (server/query-changed tableprops))
                 :class    (dom/classes-pairs :text-info sorted?)}
                (if sorted?
                  (if (= :asc direction)
                    [icons/svg-sort-up-alt]
                    [icons/svg-sort-down])

                  ;; Inactive state
                  ;; TODO: something better ?
                  [icons/svg-arrow-down])]))

           (when autofilter?
             [filters/filter-popover tableprops column])])])]))

(defn grid-table-header [{:keys [local-columns
                                 options] :as tableprops}]
  (let [has-actions? (seq (:actions options))]
    ;; TODO: configurable ?
    [:thead.sticky-header
     [:tr
      (when (or (:selectable? options)
                has-actions?)
        [:th
         {:style {:width (if has-actions?
                           "100px"
                           "2rem")}}
         [header-actions-cell tableprops]])

      (into [:<>]
            (for [column local-columns
                  :when (:show? column)]
              [header-cell-fn tableprops column]))]]))

(defn action-button [{:keys [label action]} row]
  [:button.btn.btn-sm.btn-pt-success.text-nowrap
   {:on-click (fn [e]
                (dom/prevent-default e)
                (action row))}
   label])

(defn body-actions-cell [{{:keys [selectable? actions]} :options
                          selected?                     :selected?
                          toggle-selection              :toggle-selection}
                         row]
  [:div.d-flex.justify-content-between.align-items-center
   [:div.d-flex.justify-content-start.me-2
    (when selectable?
      [row-selector {:selected? (selected? (:id row))
                     :on-change (fn [_]
                                  (toggle-selection (:id row)))}])]
   [:div.d-flex.justify-content-end
    (doall
      (for [[i action] (map-indexed vector actions)]
        ^{:key (str i)} [action-button action row]))]])

(defn nulls-last-sort
  [direction text-a text-b]
  (try
    ;; NULLS last sort
    (cond
      (or (nil? text-a)
          (str/blank? text-a))
      1

      (or (nil? text-b)
          (str/blank? text-b))
      -1

      :else
      (case direction
        :asc
        (compare text-a text-b)
        :desc
        (compare text-b text-a)))
    (catch js/Error e
      ;; Compare can throw an error (e.g. comparing string to number)
      ;; do not crash application in that case - just log an error and treat rows as equal
      (js/console.warn e "error in sorting" (type text-a) (type text-b))
      0)))

;; TODO: new facility on fields registration ?
(defn- internal-compare [column-info direction a b]
  (let [text-a (gc/cell-text column-info a)
        text-b (gc/cell-text column-info b)]
    (nulls-last-sort direction text-a text-b)))

(comment
  {:editing-cells {[1 :priority] {:value "asd"
                                  :saving? false}}})

(defn grid-cell [_ _ _ _]
  (let [node-ref (atom nil)]
    (fn [{:keys [local-state-updating
                 options
                 on-edit-save] :as tableprops}
         column row row-id]
      (let [{:keys [editable? text-align field]} column
            coords          [row-id field]
            editing-state   (get-in @local-state-updating [:editing-cells coords])
            editing?        (some? editing-state)
            saving?         (:saving? editing-state)
            editing-value   (:value editing-state)

            stop-edit-fn    (fn [stop?]
                              (if stop?
                                (swap! local-state-updating update :editing-cells dissoc coords)
                                (swap! local-state-updating update-in [:editing-cells coords]
                                       ;; reset value to row default
                                       dissoc :saving? :value)))

            status-colors   (if (:status-colors options) @(:status-colors options) {})

            cell-props      (cond-> {:ref #(reset! node-ref %)}

                                    ;; attach on-click handler for editables cells
                                    editable?
                                    (assoc :on-click (fn [e]
                                                       (let [target (.-target e)]
                                                         ;; Only handle direct clicks:
                                                         (when (and (= target @node-ref)
                                                                    (not saving?))
                                                           (swap! local-state-updating
                                                                  update :editing-cells
                                                                  assoc coords {})))))

                                    text-align
                                    (assoc :style {:text-align text-align}))]
        [:td.grid-cell
         cell-props
         [gc/body-cell
          (assoc column :status-colors status-colors
                        :container-ref node-ref
                        :editing? editing?
                        :saving? saving?
                        :editing-value editing-value
                        :on-edit-change (fn [newvalue]
                                          (swap! local-state-updating
                                                 assoc-in [:editing-cells coords :value] newvalue))
                        :on-edit-stop (fn []
                                        (swap! local-state-updating
                                               update :editing-cells dissoc coords))
                        :on-edit-save (fn [newvalue]
                                        (when on-edit-save
                                          (swap! local-state-updating
                                                 assoc-in [:editing-cells coords :saving?] true)
                                          (on-edit-save row field newvalue stop-edit-fn))))
          row]]))))

(defn grid-table-body [{:keys [local-columns
                               page-rows
                               options
                               row-props-fn
                               row-id-fn
                               local-state-updating]
                        :as   props}]
  [:tbody
   (doall
     (for [row page-rows]
       (let [row-id (if row-id-fn
                      (row-id-fn props row)
                      (str (:id row)))]
         ^{:key row-id}
         [:tr
          (when row-props-fn
            (row-props-fn props row))
          (when (or (:selectable? options)
                    (seq (:actions options)))
            [:td [body-actions-cell props row]])
          (into [:<>]
                (for [column local-columns
                      :when (:show? column)]
                  [grid-cell props column row row-id]))])))])

(defn grid-table [{:keys [resizable?] :as props}]
  [:div.table-div
   [:table.table.table-bordered.table-striped.align-middle
    {:class (dom/classes-pairs :resizable-table resizable?)}
    [grid-table-header props]
    [grid-table-body props]]])

(defn grid-footer [{:keys [total-count
                           current-page
                           per-page]
                    :as tableprops}]
  [:div.grid-footer
   [:div.left-section.nowrap
    (let [[start-index end-index] (pagination/get-page-indexes total-count current-page per-page)]
      (if (> total-count 0)
        [:span.centered
         "Showing "
         (inc start-index) " - " end-index
         " of " total-count]

        [:span.centered
         "No rows found"]))]
   [:div.right-section
    [pagination-control tableprops "lower"]]])

(defn- process-rows [rows local-state-updating local-columns]
  (let [[sort-column direction] (:sort-settings @local-state-updating)

        ;; Apply filters
        rows (filters/apply-filters rows local-state-updating local-columns)

        ;; Apply sort
        rows (if sort-column
               (let [column-info (first (filter #(= (:field %) sort-column) local-columns))]
                 (sort (fn [x y]
                         (internal-compare column-info direction x y))
                       rows))

               rows)]

    (vec rows)))

(defn process-columns [all-columns columns-order columns-visibility columns-widths]
  (let [columns-order (or columns-order
                          (mapv :field (sort-by :order all-columns)))

        columns-visibility (merge
                             ;; Default visibility map
                             (into {}
                                   (->> all-columns
                                        (filter #(false? (:default-show? % true)))
                                        (mapv (fn [c]
                                                [(:field c) false]))))
                             columns-visibility)

        ;; It's possible to have more columns in all-columns than in local collections
        columns-order (if (> (count all-columns) (count columns-order))
                        ;; Append new columns at the end of collection ?
                        ;; TODO: other merging strategy ?
                        ;; something like default-order custom-order fields ?
                        (let [all (set (map :field all-columns))
                              local (set columns-order)
                              new (clojure.set/difference all local)]
                          (apply conj columns-order new))

                        columns-order)

        ;; We need to merge all-columns list and our local settings overrides here
        local-columns (reduce
                        (fn [acc id]
                          ;; Search for column definition
                          (if-let [column (first (filter #(= (:field %) id) all-columns))]
                            ;; shown by default
                            (let [show? (boolean (get columns-visibility id true))]
                              (conj acc (assoc column :show? show?
                                                      ;; Width should always be present
                                                      :width (or (get columns-widths id)
                                                                 (:default-width column)
                                                                 fallback-column-width))))

                            ;; Non-existent column (removed by server ?)
                            acc))
                        []
                        columns-order)]
    local-columns))

;; Server-side pagination support
;;
;; on-query-changed (grid-query) - should send request to server and pass data back to the grid
;; total-count - should indicate total count of rows
;; current-page - should indicate currently selected page
;;

(defn- set-current-page! [local-state-updating page]
  (swap! local-state-updating assoc :current-page page))

;; TODO: document tableprops
(defn grid [{:keys [columns-widths-load
                    columns-settings-load
                    autofilters-load
                    per-page-load
                    current-page-load
                    sort-order-load
                    on-query-changed
                    resizable?
                    local-state-updating]
             :as   tableprops}]
  (let [;; Use external state atom if provided
        local-state-updating (or local-state-updating
                                 (reagent/atom {}))
        table-container-ref (atom nil)]

    (when (and resizable? columns-widths-load)
      (swap! local-state-updating assoc :columns-widths (columns-widths-load)))

    ;; TODO: move to filters namespace
    (when autofilters-load
      (swap! local-state-updating assoc :autofilters-state (autofilters-load)))

    (when per-page-load
      (swap! local-state-updating assoc :per-page (per-page-load)))

    (when current-page-load
      (swap! local-state-updating assoc :current-page (current-page-load)))

    (when sort-order-load
      (swap! local-state-updating assoc :sort-settings (sort-order-load)))

    ;; Initial data load - will reuse stored autofilters and ordering
    (when on-query-changed
      (on-query-changed (server/build-query {:local-state-updating local-state-updating} 1)))

    ;; Load data
    ;; TODO: merge with widths ?
    (when columns-settings-load
      (when-let [data (columns-settings-load)]
        (swap! local-state-updating
               merge
               (select-keys data [:columns-order
                                  :columns-visibility]))))

    (fn [{:keys [columns
                 data
                 loading?

                 ;; server-side data pagination support
                 on-query-changed
                 total-count]
          :as   tableprops}]
      (let [local-columns (process-columns @columns
                                           (:columns-order @local-state-updating)
                                           (:columns-visibility @local-state-updating)
                                           (:columns-widths @local-state-updating))
            tableprops (assoc tableprops :local-state-updating local-state-updating)

            ;; Support both local data/server-side paging
            local-rows    (if on-query-changed
                            @data
                            ;; Preprocess data if not server-side cursor
                            (process-rows @data local-state-updating local-columns))

            ;; For server-side pagination - use provided count, otherwise - count rows in collection
            total-count (if on-query-changed
                          total-count
                          (count local-rows))

            per-page (server/get-per-page tableprops)

            ;; We can have both user-provided or internally managed current page var
            current-page (server/get-current-page tableprops)

            total-pages (pagination/get-total-pages total-count per-page)

            ;; reset current page if filters caused dataset to shrink
            current-page (if (> current-page total-pages)
                           total-pages
                           current-page)

            ;; What to show on current page
            page-rows     (if on-query-changed
                            local-rows
                            (pagination/get-current-page-items local-rows current-page per-page))

            tableprops (assoc tableprops
                         :local-columns local-columns

                         :total-count total-count
                         :page-rows page-rows

                         ;; Propagate state as props with default values
                         :table-container-ref table-container-ref
                         :per-page per-page
                         :current-page current-page
                         :set-current-page (reagent/partial set-current-page! local-state-updating))]
        [:div.grid-container
         {:ref #(reset! table-container-ref %)}
         (when @loading?
           [spinners/overlay-spinner {:style {:width "5rem"
                                              :height "5rem"}}])

         [grid-toolbar tableprops]
         [grid-table tableprops]
         [grid-footer tableprops]]))))
